/**
 * @file DateRangePicker 日期区间
 * @description 自定义月历选起止日；值用 `YYYY-MM-DD`；Portal + 视口边界 + fade。
 *
 * @whenToUse 报表筛选、入住离店、统计区间。
 * @whenNotToUse 单日（用 DatePicker）。
 */

import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarRange } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { formatDate, parseDate } from '@/lib/date.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { Calendar } from '@/components/ui/calendar.tsx'

export type DateRangeValue = {
  /** 起始 `YYYY-MM-DD` */
  from?: string
  /** 结束 `YYYY-MM-DD` */
  to?: string
}

export type DateRangePickerProps = {
  /** 当前区间 */
  value?: DateRangeValue
  /** 区间变化 */
  onChange?: (value: DateRangeValue) => void
  /** 占位 */
  placeholder?: string
  /** 禁用 */
  disabled?: boolean
  /** 校验失败 */
  invalid?: boolean
  className?: string
  id?: string
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = '选择日期区间',
  disabled = false,
  invalid = false,
  className,
  id,
}: DateRangePickerProps) {
  const panelId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)
  const [hoverDay, setHoverDay] = useState<Date | null>(null)

  const from = value?.from ? parseDate(value.from) : null
  const to = value?.to ? parseDate(value.to) : null
  const [viewMonth, setViewMonth] = useState(
    () => from ?? new Date(),
  )

  const label =
    value?.from && value?.to
      ? `${value.from} ~ ${value.to}`
      : value?.from
        ? `${value.from} ~ …`
        : ''

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect(), 4, 288))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      updateBox()
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleSelectDay = (day: Date) => {
    const key = formatDate(day)
    if (!value?.from || (value.from && value.to)) {
      onChange?.({ from: key, to: undefined })
      return
    }
    if (key < value.from) {
      onChange?.({ from: key, to: value.from })
      setOpen(false)
      return
    }
    onChange?.({ from: value.from, to: key })
    setOpen(false)
  }

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'inline-flex h-9 w-full items-center justify-between gap-2 rounded-lg border-2 bg-raised px-3 text-left text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-danger' : 'border-border',
          className,
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            label ? 'text-foreground' : 'text-muted',
          )}
        >
          {label || placeholder}
        </span>
        <CalendarRange className="size-4 shrink-0 text-muted" aria-hidden />
      </button>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={floatingPanelStyle(box)}
            >
              <Calendar
                viewMonth={viewMonth}
                onViewMonthChange={setViewMonth}
                rangeFrom={from}
                rangeTo={to}
                hoverDay={hoverDay}
                onHoverDayChange={setHoverDay}
                onSelectDay={handleSelectDay}
              />
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
